/* Hand-drawn SVG scenery — a mosque skyline over desert dunes, a camel
   caravan, palms, and a sky that follows the prayer day (Athan Pro / Prayerly
   style). Pure SVG: crisp at any size, themable, ~5KB, no image downloads. */
import { memo, useId } from 'react';
import { dayKeyIn, timesFor, type PrayerSettings } from '@shared/prayer';

export type Phase = 'night' | 'dawn' | 'day' | 'afternoon' | 'dusk';

export function phaseAt(s: PrayerSettings, now = new Date()): Phase {
  const t = timesFor(s, dayKeyIn(s.tz, now));
  const n = now.getTime();
  if (n < t.fajr.getTime() || n >= t.isha.getTime()) return 'night';
  if (n < t.sunrise.getTime() + 20 * 60_000) return 'dawn';
  if (n < t.asr.getTime()) return 'day';
  if (n < t.maghrib.getTime() - 10 * 60_000) return 'afternoon';
  return 'dusk';
}

const SKY: Record<Phase, { top: string; mid: string; low: string; dune1: string; dune2: string; ink: string; glow: string }> = {
  night: { top: '#050b1a', mid: '#0b1f3a', low: '#173a55', dune1: '#0f2236', dune2: '#081626', ink: '#04101c', glow: '#f6e7b4' },
  dawn: { top: '#1b2551', mid: '#7c4b78', low: '#f2a27a', dune1: '#7b4b4d', dune2: '#4f2f3d', ink: '#2a1828', glow: '#ffd9a0' },
  day: { top: '#2f8fc9', mid: '#79c3e2', low: '#d9efe9', dune1: '#e2b877', dune2: '#c99252', ink: '#2c4a3e', glow: '#fff4c2' },
  afternoon: { top: '#3a77a8', mid: '#e3b56b', low: '#f6d79a', dune1: '#d79b52', dune2: '#b4743a', ink: '#3b2a1a', glow: '#ffe1a1' },
  dusk: { top: '#1d1b45', mid: '#8a3b5c', low: '#f08a4b', dune1: '#7a3d35', dune2: '#4a2329', ink: '#1e0f18', glow: '#ffc27a' },
};

/* Deterministic star field. */
const STARS = Array.from({ length: 46 }, (_, i) => {
  const r = (n: number) => (Math.sin(i * 12.9898 + n * 78.233) * 43758.5453) % 1;
  return { x: Math.abs(r(1)) * 400, y: Math.abs(r(2)) * 92, s: 0.4 + Math.abs(r(3)) * 1.1, o: 0.35 + Math.abs(r(4)) * 0.6 };
});

function Camel({ x, y, scale = 1, flip = false, rider = false }: { x: number; y: number; scale?: number; flip?: boolean; rider?: boolean }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${flip ? -scale : scale} ${scale})`}>
      {/* body, hump, neck and head facing right; legs mid-stride */}
      <path d="M2 14c1-5 5-8 10-7 2-6 9-8 13-3 2-2 6-2 8 1l4 1c1-4 2-8 5-9 2-1 4 0 4 2l-1 1h-2c-1 3-2 8-5 11-1 2-3 3-5 3v12h-2l-1-10h-9l-2 10h-2l1-10c-2 0-4-1-5-1l-2 11H9l1-11c-3 0-5-1-7 2H1z" />
      {rider && <path d="M17 5c0-3 2-5 4-5s3 2 3 4l-1 3h-5z" />}
    </g>
  );
}

function Palm({ x, y, h = 30 }: { x: number; y: number; h?: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path d={`M0 0c1-${h * 0.4} 3-${h * 0.75} 6-${h}`} strokeWidth="2.2" fill="none" stroke="currentColor" strokeLinecap="round" />
      <g transform={`translate(6 -${h})`}>
        {[-150, -110, -60, -20, 25].map((a) => (
          <path key={a} transform={`rotate(${a})`} d="M0 0c4-3 10-3 15 1-5-1-10 0-15 2z" />
        ))}
      </g>
    </g>
  );
}

/* Mosque: central dome on a drum, two minarets with balconies and crescent
   finials, small side domes and arched windows. Drawn in a 220×110 box. */
function Mosque() {
  const crescent = (cx: number, cy: number, r: number) => (
    <path d={`M${cx} ${cy - r}a${r} ${r} 0 1 0 0 ${2 * r}a${r * 0.8} ${r * 0.8} 0 1 1 0-${2 * r}z`} />
  );
  return (
    <g>
      {/* minarets */}
      {[18, 190].map((mx) => (
        <g key={mx}>
          <rect x={mx} y="18" width="10" height="92" />
          <rect x={mx - 3} y="44" width="16" height="4" rx="1" />
          <rect x={mx - 2} y="72" width="14" height="3" rx="1" />
          <path d={`M${mx - 1} 18 L${mx + 5} 2 L${mx + 11} 18z`} />
          {crescent(mx + 5, -3, 2.6)}
        </g>
      ))}
      {/* side domes */}
      <path d="M44 80a20 18 0 0 1 40 0z" />
      <path d="M136 80a20 18 0 0 1 40 0z" />
      {/* main drum + dome (slightly onion-shaped, pointed) */}
      <rect x="72" y="58" width="76" height="14" />
      <path d="M68 60c0-26 20-40 42-48 22 8 42 22 42 48z" />
      <rect x="108.5" y="4" width="3" height="10" />
      {crescent(110, 0, 3.4)}
      {/* prayer hall */}
      <rect x="36" y="78" width="148" height="32" />
    </g>
  );
}

function MosqueWindows({ lit }: { lit: string }) {
  return (
    <g fill={lit} opacity="0.85">
      {[52, 68, 84, 124, 140, 156].map((x) => <path key={x} d={`M${x} 106v-12a4 4 0 0 1 8 0v12z`} />)}
      <path d="M100 110v-18a10 10 0 0 1 20 0v18z" />
    </g>
  );
}

export const SkyScene = memo(function SkyScene({ phase, className = '', caravan = true, mosque = true, height = 180, lift = 0 }: { phase: Phase; className?: string; caravan?: boolean; mosque?: boolean; height?: number | string; /** raise the scene, filling below with sand (for content panels) */ lift?: number }) {
  const id = useId().replace(/:/g, '');
  const c = SKY[phase];
  const night = phase === 'night' || phase === 'dusk' || phase === 'dawn';
  const sunY = phase === 'day' ? 34 : phase === 'afternoon' ? 58 : phase === 'dawn' ? 96 : 92;
  const sunX = phase === 'afternoon' ? 300 : phase === 'dawn' ? 70 : phase === 'dusk' ? 320 : 250;
  return (
    /* The scene keeps its natural proportions, pinned to the bottom and capped
       at phone size (centred) — a taller container shows more sky, a wider one
       more desert: the sky and dunes are drawn far past the viewBox and spill
       out (overflow visible) to the edges, so the mosque never balloons. */
    <div className={`relative overflow-hidden ${className}`} style={{ height, background: `linear-gradient(180deg, ${c.top}, ${c.top})` }} aria-hidden>
    {lift > 0 && <div className="absolute inset-x-0 bottom-0" style={{ height: lift + 2, background: c.dune2 }} />}
    <svg viewBox="0 0 400 180" preserveAspectRatio="xMidYMax meet" overflow="visible" className="absolute left-1/2 block -translate-x-1/2"
      style={{ bottom: lift, width: 'min(100%, 520px)', height: 'auto', aspectRatio: '400 / 180' }}>
      <defs>
        <linearGradient id={`sky${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c.top} />
          <stop offset="0.6" stopColor={c.mid} />
          <stop offset="1" stopColor={c.low} />
        </linearGradient>
        <radialGradient id={`glow${id}`}>
          <stop offset="0" stopColor={c.glow} stopOpacity="0.9" />
          <stop offset="1" stopColor={c.glow} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="-2400" width="5200" height="180" fill={`url(#sky${id})`} />

      {(phase === 'night' || phase === 'dusk') && STARS.map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r={s.s} fill="#fff" opacity={phase === 'dusk' ? s.o * 0.5 : s.o} />
      ))}

      {phase === 'night' ? (
        <g transform="translate(318 40)">
          <circle r="26" fill={`url(#glow${id})`} opacity="0.5" />
          {/* crescent moon */}
          <path d="M0-15a15 15 0 1 0 10 26a12 12 0 1 1-10-26z" fill={c.glow} />
          <path d="M22-6l1.6 3.4 3.7.4-2.8 2.5.8 3.7-3.3-1.9-3.3 1.9.8-3.7-2.8-2.5 3.7-.4z" fill={c.glow} opacity="0.9" />
        </g>
      ) : (
        <g>
          <circle cx={sunX} cy={sunY} r="44" fill={`url(#glow${id})`} opacity={night ? 0.7 : 0.6} />
          <circle cx={sunX} cy={sunY} r={phase === 'day' ? 13 : 16} fill={c.glow} />
        </g>
      )}

      {/* far dune */}
      <path d="M-2400 140H0c50-18 90-20 140-8s90 14 150-4 80-12 110-6c40 4 80 6 120 4H2800v200H-2400z" fill={c.dune1} opacity="0.9" />

      {mosque && (
        <g transform="translate(150 50) scale(0.95)" fill={c.ink}>
          <Mosque />
          <MosqueWindows lit={night ? '#ffd27a' : c.dune1} />
        </g>
      )}

      {/* palms */}
      <g fill={c.ink} color={c.ink}>
        <Palm x={78} y={152} h={34} />
        <Palm x={98} y={156} h={26} />
        <Palm x={372} y={154} h={30} />
      </g>

      {/* near dune */}
      <path d="M-2400 162H0c40-12 80-14 130-6s110 10 170-2 70-6 100-2c50 4 100 4 150 0H2800v200H-2400z" fill={c.dune2} />

      {caravan && (
        <g fill={c.ink}>
          <Camel x={10} y={124} scale={1.05} rider />
          <Camel x={58} y={132} scale={0.86} />
          <Camel x={96} y={138} scale={0.7} />
        </g>
      )}
    </svg>
    {(phase === 'night' || phase === 'dusk') && (
      <svg className="absolute inset-x-0 top-0 h-1/2 w-full" viewBox="0 0 400 100" preserveAspectRatio="none">
        {STARS.slice(0, 24).map((st, i) => <circle key={i} cx={st.x} cy={st.y * 0.9} r={st.s * 0.8} fill="#fff" opacity={st.o * (phase === 'dusk' ? 0.4 : 0.8)} />)}
      </svg>
    )}
    </div>
  );
});
